import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Sparkles, Flame, BookOpen, Brain, Dices, Target, Zap, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
import { useStudyStore } from '../store/studyStore';
import { fetchReviewQueue, submitReview, setWordStatus } from '../api/progress';
import { fetchEnrichment } from '../api/enrich';
import { fetchSheets } from '../api/words';
import { apiClient } from '../api/client';
import { AudioButton } from './AudioButton';
import type { Word, Enrichment, Rating } from '../types';

// Fallback XP per rating, used only while offline (server is authoritative).
const XP_MAP: Record<Rating, number> = {
  again: 0,
  hard: 5,
  good: 10,
  easy: 15,
};

// Fallback labels for first-time cards; real values come from word.previews.
const INTERVAL_LABELS: Record<Rating, string> = {
  again: '<10 min',
  hard: '1 day',
  good: '1 day',
  easy: '2 days',
};

const RATING_CONFIG: Record<Rating, { label: string; shortcut: string; color: string; bg: string; border: string; hoverBg: string }> = {
  again: {
    label: 'Again',
    shortcut: '1',
    color: 'text-red',
    bg: 'bg-red-muted',
    border: 'border-red/30',
    hoverBg: 'hover:bg-red-muted',
  },
  hard: {
    label: 'Hard',
    shortcut: '2',
    color: 'text-amber',
    bg: 'bg-amber-muted',
    border: 'border-amber/30',
    hoverBg: 'hover:bg-amber-muted',
  },
  good: {
    label: 'Good',
    shortcut: '3',
    color: 'text-green',
    bg: 'bg-green-muted',
    border: 'border-green/30',
    hoverBg: 'hover:bg-green-muted',
  },
  easy: {
    label: 'Easy',
    shortcut: '4',
    color: 'text-blue',
    bg: 'bg-blue-muted',
    border: 'border-blue/30',
    hoverBg: 'hover:bg-blue-muted',
  },
};

const CARD_COUNTS = [5, 10, 15, 20, 25, 30, 50, 100];

const STRATEGIES: { key: 'due' | 'random' | 'weak' | 'new'; label: string; icon: React.ElementType; desc: string }[] = [
  { key: 'due', label: 'Due First', icon: Target, desc: 'Words scheduled for review' },
  { key: 'weak', label: 'Weak Words', icon: Brain, desc: 'Words you struggle with' },
  { key: 'new', label: 'New Words', icon: Sparkles, desc: 'Words you have not seen' },
  { key: 'random', label: 'Random', icon: Dices, desc: 'Shuffle from the deck' },
];

export const StudySession: React.FC = () => {
  const navigate = useNavigate();
  const {
    queue,
    currentIndex,
    isFlipped,
    sessionStats,
    summary,
    setQueue,
    nextCard,
    prevCard,
    flipCard,
    submitRating: storeSubmitRating,
    requeueCurrent,
    removeCurrent,
    resetSession,
    setSummary,
  } = useStudyStore();

  const [configured, setConfigured] = useState(false);
  const [cardCount, setCardCount] = useState(20);
  const [customCount, setCustomCount] = useState('');
  const [selectedSheet, setSelectedSheet] = useState('');
  const [strategy, setStrategy] = useState<'due' | 'random' | 'weak' | 'new'>('due');
  const [sheets, setSheets] = useState<{ sheet: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrichment, setEnrichment] = useState<Enrichment | null>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [shake, setShake] = useState(false);
  const cardStartTime = useRef<number>(Date.now());
  const sceneRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSheets().then(setSheets).catch(() => {});
    resetSession();
    setConfigured(false);
    setLoading(false);
  }, [resetSession]);

  const startSession = async () => {
    const count = customCount ? parseInt(customCount, 10) : cardCount;
    if (!count || count < 1) return;
    setLoading(true);
    try {
      const words = await fetchReviewQueue({
        limit: count,
        sheet: selectedSheet || undefined,
        strategy,
      });
      setQueue(words);
      setConfigured(true);
    } catch {
      setQueue([]);
    } finally {
      setLoading(false);
    }
  };

  const currentWord: Word | undefined = queue[currentIndex];

  useEffect(() => {
    if (currentWord && !currentWord.enrichment) {
      setEnrichmentLoading(true);
      fetchEnrichment(currentWord.id, currentWord.word, currentWord.definition)
        .then((data) => setEnrichment(data))
        .catch(() => setEnrichment(null))
        .finally(() => setEnrichmentLoading(false));
    } else if (currentWord?.enrichment) {
      setEnrichment(currentWord.enrichment);
    } else {
      setEnrichment(null);
    }
    cardStartTime.current = Date.now();
  }, [currentWord]);

  // 3D tilt on mouse move
  useEffect(() => {
    const scene = sceneRef.current;
    const inner = innerRef.current;
    if (!scene || !inner) return;

    const handleMove = (e: MouseEvent) => {
      if (inner.classList.contains('flipped')) return;
      const rect = scene.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      inner.style.setProperty('--tilt-y', (dx * 4) + 'deg');
      inner.style.setProperty('--tilt-x', (-dy * 3) + 'deg');
    };

    const handleLeave = () => {
      inner.style.setProperty('--tilt-y', '0deg');
      inner.style.setProperty('--tilt-x', '0deg');
    };

    scene.addEventListener('mousemove', handleMove);
    scene.addEventListener('mouseleave', handleLeave);
    return () => {
      scene.removeEventListener('mousemove', handleMove);
      scene.removeEventListener('mouseleave', handleLeave);
    };
  }, [currentWord]);

  const handleFlip = useCallback(() => {
    flipCard();
  }, [flipCard]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setDirection('prev');
      prevCard();
    }
  }, [currentIndex, prevCard]);

  const handleNext = useCallback(() => {
    if (currentIndex < queue.length - 1) {
      setDirection('next');
      nextCard();
    }
  }, [currentIndex, queue.length, nextCard]);

  const serverStreak = useRef<number>(0);
  const [goalToast, setGoalToast] = useState('');
  const goalCelebrated = useRef(false);

  const finishSession = useCallback(
    async (lastRating: Rating, lastXp: number, countLast = true) => {
      const totalReviewed = sessionStats.wordsReviewed + (countLast ? 1 : 0);
      // "Hard" means recalled with effort — it counts as correct.
      const correctCount =
        sessionStats.ratings.hard +
        sessionStats.ratings.good +
        sessionStats.ratings.easy +
        (countLast && lastRating !== 'again' ? 1 : 0);
      const accuracy = totalReviewed > 0 ? (correctCount / totalReviewed) * 100 : 0;
      const xpTotal = sessionStats.xpEarned + lastXp;

      // Record the session so history, heatmap, and achievements update.
      let achievements: { badge_key: string; earned_at: string }[] = [];
      try {
        const { data } = await apiClient.post('/gamification/study', {
          session_type: 'flashcards',
          mode: 'flashcard',
          words_studied: totalReviewed,
          correct_count: correctCount,
          total_count: totalReviewed,
          xp_earned: xpTotal,
          duration_seconds: Math.round((Date.now() - sessionStats.startTime) / 1000),
        });
        const badges: string[] = data?.gamification?.new_badges ?? [];
        achievements = badges.map((b) => ({ badge_key: b, earned_at: '' }));
        serverStreak.current = data?.gamification?.current_streak ?? serverStreak.current;
      } catch {
        // Offline — session record will be missing, but reviews are queued.
      }

      setSummary({
        words_studied: totalReviewed,
        xp_earned: xpTotal,
        accuracy: Math.round(accuracy),
        new_streak: serverStreak.current,
        streak_maintained: serverStreak.current > 0,
        achievements_unlocked: achievements,
      });
    },
    [sessionStats, setSummary]
  );

  const handleRating = useCallback(
    async (rating: Rating) => {
      if (!currentWord) return;
      const responseTime = Date.now() - cardStartTime.current;

      if (rating === 'again') {
        setShake(true);
        setTimeout(() => setShake(false), 400);
        // Failed cards come back at the end of this session (once).
        requeueCurrent();
      }

      const result = await submitReview({
        word_id: currentWord.id,
        rating,
        response_time_ms: responseTime,
      });
      // Server-computed XP when online; local estimate while offline.
      const xp = result ? result.xp_earned : XP_MAP[rating];
      storeSubmitRating(rating, xp);
      if (result) {
        serverStreak.current = result.current_streak;
        if (result.daily_goal_met && !goalCelebrated.current) {
          goalCelebrated.current = true;
          setGoalToast('Daily goal complete! +25 XP bonus');
          setTimeout(() => setGoalToast(''), 3500);
        }
      }

      setDirection('next');

      // Note: queue may have grown via requeueCurrent above.
      const queueLen = useStudyStore.getState().queue.length;
      if (currentIndex >= queueLen - 1) {
        await finishSession(rating, xp);
      } else {
        nextCard();
      }
    },
    [currentWord, currentIndex, storeSubmitRating, nextCard, requeueCurrent, finishSession]
  );

  const handleKnowThis = useCallback(async () => {
    if (!currentWord) return;
    try {
      await setWordStatus(currentWord.id, 'mastered');
    } catch {
      return; // keep the card if the server rejected it
    }
    const queueLen = useStudyStore.getState().queue.length;
    if (queueLen <= 1) {
      await finishSession('easy', 0, false);
    } else {
      removeCurrent();
    }
  }, [currentWord, removeCurrent, finishSession]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (summary) return;
      if (!configured) return;
      if (e.code === 'Space') {
        e.preventDefault();
        handleFlip();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
      if (!isFlipped) return;
      if (e.key === '1') handleRating('again');
      if (e.key === '2') handleRating('hard');
      if (e.key === '3') handleRating('good');
      if (e.key === '4') handleRating('easy');
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleFlip, handleRating, handlePrev, handleNext, isFlipped, summary, configured]);

  // beforeunload guard during active sessions
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (queue.length > 0 && !summary && configured) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [queue.length, summary, configured]);

  if (!configured) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-8 md:p-10 max-w-md w-full"
          style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)' }}
        >
          <div className="text-center mb-6">
            <BookOpen size={36} className="text-gold mx-auto mb-4 opacity-60" />
            <h2 className="font-display text-2xl text-cream mb-1">Study Session</h2>
            <p className="text-sm text-text">Configure your learning preferences.</p>
          </div>

          <div className="space-y-4">
            {/* Card count */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Card Count</label>
              <div className="flex gap-2">
                <select
                  value={customCount ? '' : cardCount}
                  onChange={(e) => {
                    setCardCount(parseInt(e.target.value, 10));
                    setCustomCount('');
                  }}
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
                >
                  {CARD_COUNTS.map((c) => (
                    <option key={c} value={c}>{c} cards</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  placeholder="Custom"
                  min={1}
                  max={200}
                  className="w-24 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold placeholder:text-text-dim"
                />
              </div>
            </div>

            {/* Deck */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Deck</label>
              <select
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream outline-none focus:border-gold"
              >
                <option value="">All Words ({sheets.reduce((a, s) => a + s.count, 0)})</option>
                {sheets.map((s) => (
                  <option key={s.sheet} value={s.sheet}>
                    {s.sheet.startsWith('Practice') || s.sheet.startsWith('Pairs')
                      ? s.sheet
                      : `Sheet ${s.sheet}`} ({s.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Strategy */}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-text-dim mb-2">Selection Strategy</label>
              <div className="grid grid-cols-2 gap-2">
                {STRATEGIES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setStrategy(s.key)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all ${
                        strategy === s.key
                          ? 'border-gold-dim bg-gold-glow text-gold'
                          : 'border-border bg-surface text-text-dim hover:text-text-hi hover:bg-surface-1'
                      }`}
                    >
                      <Icon size={15} />
                      <div>
                        <p className="text-xs font-medium">{s.label}</p>
                        <p className="text-[10px] opacity-70">{s.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={startSession}
              disabled={loading}
              className="w-full py-3.5 rounded-xl btn-gold text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <Zap size={16} />
              )}
              Start Studying
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-dim">
          <div className="w-4 h-4 border-2 border-white/10 border-t-gold rounded-full animate-spin" />
          Loading your review queue...
        </div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <Sparkles size={40} className="text-gold mx-auto mb-4 opacity-50" />
          <h2 className="font-display text-2xl text-text-hi mb-2">All Caught Up!</h2>
          <p className="text-text text-sm mb-6 max-w-xs mx-auto">
            No words are available with your current settings. Try a different deck or strategy.
          </p>
          <button
            onClick={() => setConfigured(false)}
            className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold"
          >
            Change Settings
          </button>
        </div>
      </div>
    );
  }

  if (summary) {
    return <SessionComplete summary={summary} onContinue={() => { resetSession(); navigate('/dashboard'); }} />;
  }

  const progress = ((currentIndex) / queue.length) * 100;

  return (
    <div className="h-full flex flex-col items-center justify-start md:justify-center px-4 md:px-8 py-4 md:py-6 relative pb-28 md:pb-6 overflow-y-auto md:overflow-visible">
      {/* Progress bar */}
      <div className="w-full max-w-[700px] mb-3 md:mb-5">
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, var(--gold) 0%, var(--green) 100%)',
              boxShadow: '0 0 12px rgba(108,99,255,0.35)',
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[11px] text-text-dim font-medium">
            {currentIndex + 1} / {queue.length}
          </span>
          <span className="text-[11px] text-text-dim font-medium">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Card */}
      <div className="w-full max-w-[700px] flex-1 flex flex-col items-center justify-center min-h-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentWord.id}
            initial={{ opacity: 0, x: direction === 'next' ? 24 : -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction === 'next' ? -24 : 24 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="w-full"
          >
            <div
              ref={sceneRef}
              className={`card-scene mx-auto ${shake ? 'animate-shake' : ''}`}
              style={{ aspectRatio: window.innerWidth < 768 ? '16/11' : '700/400', maxHeight: window.innerWidth < 768 ? '40vh' : '50vh' }}
              onClick={handleFlip}
            >
              <div ref={innerRef} className={`card-inner ${isFlipped ? 'flipped' : ''}`}>
                {/* Front — word only */}
                <div className="card-face card-front">
                  <div className="absolute top-3.5 right-4 z-10">
                    <AudioButton word={currentWord.word} />
                  </div>
                  <h2 className="font-display text-[clamp(32px,5.5vw,50px)] font-bold text-cream text-center leading-tight relative z-10">
                    {currentWord.word}
                  </h2>
                  <div className="flip-prompt">
                    <RotateCcw size={12} />
                    Tap or Space to flip
                  </div>
                </div>

                {/* Back */}
                <div className="card-face card-back">
                  {enrichmentLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/10 border-t-gold rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-display text-xl md:text-2xl text-cream">{currentWord.word}</h3>
                          <p className="text-xs text-text-dim mt-0.5">{currentWord.part_of_speech}</p>
                        </div>
                        <AudioButton word={currentWord.word} />
                      </div>

                      <div className="back-section">
                        <div className="back-label">Definition</div>
                        <div className="back-content">
                          {currentWord.definition}
                        </div>
                      </div>

                      {enrichment?.mnemonic && (
                        <div className="back-section">
                          <div className="back-label">Memory Trick</div>
                          <div className="memory-trick">
                            {enrichment.mnemonic}
                          </div>
                        </div>
                      )}

                      {enrichment?.example_sentence && (
                        <div className="back-section">
                          <div className="back-label">Example</div>
                          <div className="example-line">
                            {enrichment.example_sentence}
                          </div>
                        </div>
                      )}

                      {enrichment?.synonyms && enrichment.synonyms.length > 0 && (
                        <div className="back-section">
                          <div className="back-label">Synonyms</div>
                          <div className="synonyms">
                            {enrichment.synonyms.map((s) => (
                              <span key={s} className="syn-tag">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {enrichment?.etymology && (
                        <div className="back-section">
                          <div className="back-label">Etymology</div>
                          <div className="back-content" style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                            {enrichment.etymology}
                          </div>
                        </div>
                      )}

                      {enrichment?.common_traps && enrichment.common_traps.length > 0 && (
                        <div className="back-section">
                          <div className="back-label">Common Traps</div>
                          <div className="flex flex-wrap gap-1.5">
                            {enrichment.common_traps.map((t, i) => (
                              <span key={i} className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-muted border border-amber/20 text-amber">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation arrows + I know this */}
      <div className="w-full max-w-[700px] mt-3 md:mt-4 flex items-center justify-between gap-2">
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="flex items-center gap-1 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.04] text-text-dim text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.08] hover:text-text active:scale-95"
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">Previous</span>
        </button>
        <button
          onClick={handleKnowThis}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-green/30 bg-green-muted text-green text-xs font-medium transition-all hover:brightness-125 active:scale-95"
          title="Mark as mastered and remove from your study rotation"
        >
          <BadgeCheck size={15} />
          I know this
        </button>
        <button
          onClick={handleNext}
          disabled={currentIndex >= queue.length - 1}
          className="flex items-center gap-1 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.04] text-text-dim text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.08] hover:text-text active:scale-95"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Daily goal toast */}
      <AnimatePresence>
        {goalToast && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2.5 rounded-xl glass-strong border border-green/40 text-sm text-green font-medium"
          >
            <Target size={15} />
            {goalToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rating buttons */}
      <AnimatePresence>
        {isFlipped && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-[700px] mt-3 md:mt-4"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {(Object.keys(RATING_CONFIG) as Rating[]).map((rating) => {
                const cfg = RATING_CONFIG[rating];
                const interval = currentWord.previews?.[rating] ?? INTERVAL_LABELS[rating];
                return (
                  <button
                    key={rating}
                    onClick={() => handleRating(rating)}
                    className={`flex flex-col items-center justify-center gap-0.5 py-3 md:py-3.5 rounded-xl border transition-all duration-150 active:scale-95 ${cfg.border} ${cfg.bg} ${cfg.hoverBg}`}
                    title={`${cfg.label}: next review in ${interval}`}
                  >
                    <span className={`text-sm md:text-base font-semibold ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-[10px] text-text-dim font-mono hidden md:inline">{cfg.shortcut}</span>
                    <span className="text-[10px] md:text-[9px] text-text-dim font-mono mt-0.5 opacity-70">{interval}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SessionComplete: React.FC<{ summary: import('../types').SessionSummary; onContinue: () => void }> = ({
  summary,
  onContinue,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="h-full flex items-center justify-center p-6"
    >
      <div className="glass-strong rounded-3xl p-8 md:p-10 max-w-md w-full text-center"
        style={{
          boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px rgba(0,0,0,0.6)',
        }}
      >
        <div className="w-16 h-16 rounded-full bg-gold-glow border border-gold-dim flex items-center justify-center mx-auto mb-5">
          <Sparkles size={28} className="text-gold" />
        </div>

        <h2 className="font-display text-2xl text-cream mb-1">Session Complete!</h2>
        <p className="text-sm text-text mb-6">Great work. Here&apos;s how you did:</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-cream">{summary.words_studied}</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">Words</p>
          </div>
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-gold-text">+{summary.xp_earned}</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">XP</p>
          </div>
          <div className="glass p-3 rounded-xl">
            <p className="font-mono text-lg text-green">{summary.accuracy}%</p>
            <p className="text-[10px] text-text-dim uppercase tracking-widest mt-0.5">Accuracy</p>
          </div>
        </div>

        {summary.streak_maintained && (
          <div className="flex items-center justify-center gap-2 text-amber text-sm mb-6">
            <Flame size={16} />
            <span>Streak maintained!</span>
          </div>
        )}

        {summary.achievements_unlocked.length > 0 && (
          <div className="mb-6">
            <p className="text-[10px] text-text-dim uppercase tracking-widest mb-2">Achievements Unlocked</p>
            <div className="flex flex-wrap justify-center gap-2">
              {summary.achievements_unlocked.map((ach) => (
                <span key={ach.badge_key} className="px-3 py-1 rounded-full text-xs font-medium border border-gold-dim text-gold-text bg-gold-glow">
                  {ach.badge_key.replace(/-/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        <button onClick={onContinue} className="btn-gold w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
          Continue
          <span className="text-lg leading-none">›</span>
        </button>
      </div>
    </motion.div>
  );
};
